import { useState, useEffect } from "react";
import { Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  ShoppingCart,
  Store,
  DollarSign,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Dashboard Pages
import DistributorOverview from "./DistributorOverview";
import DistributorOrders from "./DistributorOrders";
import DistributorResellers from "./DistributorResellers";
import DistributorEarnings from "./DistributorEarnings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { path: "/distributor", icon: LayoutDashboard, label: "Overview", exact: true },
  { path: "/distributor/orders", icon: ShoppingCart, label: "Orders" },
  { path: "/distributor/resellers", icon: Store, label: "My Resellers" },
  { path: "/distributor/earnings", icon: DollarSign, label: "Earnings" },
];

export default function DistributorDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [distributor, setDistributor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, getAuthHeaders());
      if (response.data.role !== "distributor") {
        toast.error("Access denied. Distributor account required.");
        navigate("/login");
        return;
      }
      setUser(response.data);
      
      // Fetch distributor details
      const distResponse = await axios.get(`${API}/distributor/me`, getAuthHeaders());
      setDistributor(distResponse.data);
    } catch (error) {
      toast.error("Please login to continue");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, getAuthHeaders());
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("session_token");
    navigate("/login");
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50/30 flex" data-testid="distributor-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white/80 backdrop-blur-xl border-r border-purple-100/50 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-purple-100/50 bg-gradient-to-r from-purple-50/50 to-transparent">
            <NavLink to="/distributor" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
                <Store className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-slate-900">
                  eDisplayroom
                </span>
                <span className="block text-[10px] text-slate-500 -mt-0.5">by Infinity Funeral Supply</span>
                <span className="block text-xs text-purple-600 font-medium">Distributor Portal</span>
              </div>
            </NavLink>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const active = isActive(item.path, item.exact);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    active 
                      ? 'bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 font-medium shadow-sm' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`w-5 h-5 ${active ? 'text-purple-600' : ''}`} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-purple-100/50 sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center">
            <button 
              className="lg:hidden p-2 mr-4"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-semibold text-slate-900">
              {navItems.find(item => isActive(item.path, item.exact))?.label || 'Distributor'}
            </h1>
          </div>
          
          {/* User Info & Logout - Top Right */}
          <div className="flex items-center gap-4">
            {(user || distributor) && (
              <div className="hidden sm:flex items-center gap-3">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center">
                  <span className="text-purple-700 font-semibold text-sm">
                    {(distributor?.name || user?.name)?.charAt(0) || 'D'}
                  </span>
                </div>
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-slate-900">{distributor?.name || user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>
            )}
            
            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 rounded-full">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Account</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 lg:p-8">
          <Routes>
            <Route index element={<DistributorOverview distributor={distributor} />} />
            <Route path="orders" element={<DistributorOrders distributor={distributor} />} />
            <Route path="resellers" element={<DistributorResellers distributor={distributor} />} />
            <Route path="earnings" element={<DistributorEarnings distributor={distributor} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
